import React, { useCallback, useEffect, useState } from "react";
import { ToolbarRaiseHandButton } from "./FluentToolbarRaiseHandButton";
import PropTypes from "prop-types";
import { LogMessageType } from "../../utils/messages";

export function useRaisedHands(scene, hubChannel) {
  const [raisedHands, setRaisedHands] = useState([]);

  /**
   * Initializes the raisedHands list using the entire presence state.
   */
  const initializeRaisedHands = useCallback(() => {
    const presenceState = hubChannel.presence.state;
    if (!presenceState) {
      setRaisedHands([]);
      return;
    }

    const initialRaised = [];

    Object.entries(presenceState).forEach(([sessionId, userData]) => {
      const { metas } = userData;
      metas.forEach(meta => {
        if (meta.hand_raised) {
          initialRaised.push({
            sessionId,
            displayName: meta.profile.displayName
          });
        }
      });
    });

    // Remove duplicates based on sessionId
    const uniqueRaised = Array.from(new Map(initialRaised.map(item => [item.sessionId, item])).values());

    setRaisedHands(uniqueRaised);
  }, [hubChannel.presence.state]);

  /**
   * Handles presence updates by adding or removing users from raisedHands.
   *
   * @param {Object} updatedPresence - The partial presence update.
   */
  const handlePresenceUpdate = useCallback(updatedPresence => {
    const { sessionId, hand_raised, profile } = updatedPresence;

    setRaisedHands(prevRaisedHands => {
      const isAlreadyRaised = prevRaisedHands.some(user => user.sessionId === sessionId);

      if (hand_raised) {
        if (!isAlreadyRaised) {
          return [...prevRaisedHands, { sessionId, displayName: profile.displayName }];
        }
      } else {
        if (isAlreadyRaised) {
          return prevRaisedHands.filter(user => user.sessionId !== sessionId);
        }
      }

      return prevRaisedHands;
    });
  }, []);

  /**
   * Event listener callback for presence updates.
   * Extracts relevant data and updates raisedHands accordingly.
   *
   * @param {Event} event - The presence_updated event.
   */
  const presenceUpdateCallback = useCallback(
    event => {
      const updatedPresence = event.detail;

      handlePresenceUpdate(updatedPresence);
    },
    [handlePresenceUpdate]
  );

  useEffect(() => {
    // Initialize with the current presence state
    initializeRaisedHands();

    // Listen for presence updates
    scene.addEventListener("presence_updated", presenceUpdateCallback);

    // Cleanup listener on unmount
    return () => {
      scene.removeEventListener("presence_updated", presenceUpdateCallback);
    };
  }, [scene, initializeRaisedHands, presenceUpdateCallback]);

  return raisedHands;
}

export function usePresence(scene, initialPresence) {
  const [presence, setPresence] = useState(initialPresence);

  const onPresenceUpdate = ({ detail: presence }) => {
    if (presence.sessionId === NAF.clientId) setPresence(presence);
  };

  useEffect(() => {
    scene.addEventListener("presence_updated", onPresenceUpdate);
    return () => scene.removeEventListener("presence_updated", onPresenceUpdate);
  }, [scene]);

  return presence;
}

export function useToggleRaisedHand(presence, hubChannel, store) {
  const onToggleHandRaised = useCallback(() => {
    if (presence.hand_raised) {
      hubChannel.lowerHand();
    } else {
      hubChannel.raiseHand();
      // Notify that a new user has raised their hand
      window.APP.messageDispatch.push(
        {
          sender: store.state.profile.displayName,
          senderSessionId: presence.sessionId
        },
        LogMessageType.handRaised
      );
    }
  }, [presence, hubChannel, store]);

  return onToggleHandRaised;
}

export function ToolbarRaiseHandButtonContainer({ store, scene, hubChannel, initialPresence, styles }) {
  const presence = usePresence(scene, initialPresence);
  const raisedHands = useRaisedHands(scene, hubChannel);

  const onToggleHandRaised = useToggleRaisedHand(presence, hubChannel, store);

  return (
    <ToolbarRaiseHandButton
      styles={styles}
      onToggleHandRaised={onToggleHandRaised}
      active={presence?.hand_raised}
      raisedHands={raisedHands}
    />
  );
}

ToolbarRaiseHandButtonContainer.propTypes = {
  scene: PropTypes.object,
  initialPresence: PropTypes.object,
  styles: PropTypes.object,
  store: PropTypes.object,
  hubChannel: PropTypes.object
};
